import { Flame, Beef, Wheat, Droplet, Clock, Utensils } from 'lucide-react';

/**
 * Lightweight in-memory meal renderer for the bulk-import preview step.
 *
 * Mirrors the visual layout of `MealView.jsx` (hero image → title/category
 * pill → macros grid → ingredients list → instructions) but reads entirely
 * from the props payload — no DB fetch, no meal_id dependency. This lets
 * admins see how a draft import will appear on the client-facing meal page
 * BEFORE it's written.
 *
 * Props:
 *   meal_input        (object)  — parsed meal-level fields from dry-run
 *   ingredients       (array)   — resolved ingredients with resolved_food_name
 *   computed_macros   (object)  — per-serving macro totals {calories,protein,carbs,fat}
 */
export default function MealPreviewRenderer({ meal_input, ingredients, computed_macros }) {
  const title = meal_input?.title || '(untitled)';
  const category = (meal_input?.meal_type || '').charAt(0).toUpperCase() + (meal_input?.meal_type || '').slice(1);
  const cookTime = meal_input?.cook_time_min || 0;
  const servings = meal_input?.servings || 1;
  const image = meal_input?.image_url;
  const instructions = meal_input?.instructions || [];
  const cookware = meal_input?.required_cookware || [];
  const tags = meal_input?.tags || [];
  const cuisine = meal_input?.cuisine;
  const proteinType = meal_input?.protein_type;
  const difficulty = meal_input?.difficulty;

  // Split required vs optional, matching the regular MealView layout
  const required = (ingredients || []).filter((i) => !i.is_optional);
  const optional = (ingredients || []).filter((i) => i.is_optional);

  const macros = computed_macros || { calories: 0, protein: 0, carbs: 0, fat: 0 };

  return (
    <div className="bg-background">
      {/* Hero */}
      <div className="relative w-full h-56 bg-muted overflow-hidden">
        {image ? (
          <img src={image} alt={title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
            (no image)
          </div>
        )}
        {category && (
          <span className="absolute bottom-4 left-4 px-3 py-1 rounded-full bg-accent text-white text-sm font-medium">
            {category}
          </span>
        )}
      </div>

      <div className="p-5 space-y-5">
        {/* Title */}
        <div>
          <h2 className="font-heading font-bold text-2xl mb-1">{title}</h2>
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            {cookTime > 0 && (
              <span className="flex items-center gap-1">
                <Clock size={12} /> {cookTime} min
              </span>
            )}
            <span>· {servings} serving{servings === 1 ? '' : 's'}</span>
            {difficulty && <span>· {difficulty}</span>}
            {cuisine && <span>· {cuisine}</span>}
            {proteinType && <span>· {proteinType}</span>}
          </div>
        </div>

        {/* Macros grid — mirrors MealView */}
        <div className="grid grid-cols-4 gap-2">
          <MacroStat icon={<Flame size={14} />} label="Cal" value={macros.calories} unit="" />
          <MacroStat icon={<Beef size={14} />} label="Protein" value={macros.protein} unit="g" />
          <MacroStat icon={<Wheat size={14} />} label="Carbs" value={macros.carbs} unit="g" />
          <MacroStat icon={<Droplet size={14} />} label="Fat" value={macros.fat} unit="g" />
        </div>

        {/* Cookware */}
        {cookware.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1">
              <Utensils size={12} /> Cookware
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {cookware.map((c) => (
                <span key={c} className="px-2 py-1 rounded-md bg-muted text-xs">{c}</span>
              ))}
            </div>
          </div>
        )}

        {/* Tags */}
        {tags.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Tags</h3>
            <div className="flex flex-wrap gap-1.5">
              {tags.map((t) => (
                <span key={t} className="px-2 py-1 rounded-md bg-accent/10 text-accent text-xs">{t}</span>
              ))}
            </div>
          </div>
        )}

        {/* Ingredients */}
        <div>
          <h3 className="font-heading font-semibold text-base mb-2">Ingredients</h3>
          {required.length === 0 && optional.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">(no ingredients)</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {required.map((ing, i) => <IngredientLine key={`r${i}`} ing={ing} />)}
              {optional.length > 0 && (
                <>
                  <li className="pt-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Optional</li>
                  {optional.map((ing, i) => <IngredientLine key={`o${i}`} ing={ing} optional />)}
                </>
              )}
            </ul>
          )}
        </div>

        {/* Instructions */}
        {instructions.length > 0 && (
          <div>
            <h3 className="font-heading font-semibold text-base mb-2">Instructions</h3>
            <ol className="space-y-2 text-sm">
              {instructions.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-accent/10 text-accent text-xs font-semibold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span className="flex-1">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}

function MacroStat({ icon, label, value, unit }) {
  return (
    <div className="bg-card border border-border/40 rounded-lg p-2 text-center">
      <div className="flex items-center justify-center gap-1 text-muted-foreground mb-0.5">
        {icon}
        <span className="text-[10px] uppercase tracking-wide">{label}</span>
      </div>
      <div className="font-semibold text-sm">{value}{unit}</div>
    </div>
  );
}

function IngredientLine({ ing, optional }) {
  const name = ing.display_text || ing.resolved_food_name || ing.core_food_id;
  const qty = ing.display_qty ? `${ing.display_qty} ${ing.display_unit_id || ''}` : `${ing.amount_grams}g`;
  const prep = ing.prep_note ? `, ${ing.prep_note}` : '';
  return (
    <li className={`flex items-start gap-2 ${optional ? 'text-muted-foreground' : ''}`}>
      <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-accent mt-2" />
      <span className="flex-1">
        <strong>{qty.trim()}</strong> {name}<span className="text-muted-foreground">{prep}</span>
      </span>
    </li>
  );
}
